/**
 * Audit Reports page — list generated reports with status, and trigger new report generation.
 * Internal experience only, accessible from the main navigation.
 */

import Badge, { type BadgeProps } from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import Table from "@cloudscape-design/components/table"
import TextFilter from "@cloudscape-design/components/text-filter"
import { useEffect, useMemo, useState } from "react"
import AuditReportModal from "@/components/AuditReportModal"
import Breadcrumb from "@/components/Navigation/Breadcrumb"
import Pagination from "@/components/Pagination/Pagination"
import { useAuth } from "@/contexts/AuthContext"
import { useNotifications } from "@/contexts/NotificationContext"
import { useAuditReportsList, useAuditReport } from "@/hooks/queries/useAuditReports"
import { useStudiesList } from "@/hooks/queries/useStudies"
import { auditApiService, type AuditReportListItem } from "@/services/auditApi"
import { downloadUrlAsFile } from "@/utils/downloadUrlAsFile"

const ITEMS_PER_PAGE = 20

function statusBadgeColor(status: string): NonNullable<BadgeProps["color"]> {
  switch (status) {
    case "COMPLETE":
      return "green"
    case "IN_PROGRESS":
      return "blue"
    case "FAILED":
      return "red"
    default:
      return "severity-neutral"
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString()
}

function formatFilters(filters: AuditReportListItem["filters"]): string {
  const parts: string[] = []
  if (filters.dateRange) {
    parts.push(`${filters.dateRange.start.slice(0, 10)} → ${filters.dateRange.end.slice(0, 10)}`)
  }
  if (filters.eventTypes?.length) {
    parts.push(`${filters.eventTypes.length} event type${filters.eventTypes.length > 1 ? "s" : ""}`)
  }
  if (filters.studyIds?.length) {
    parts.push(`${filters.studyIds.length} ${filters.studyIds.length === 1 ? "study" : "studies"}`)
  }
  return parts.length > 0 ? parts.join(" · ") : "All events"
}

export default function AuditReportsPage() {
  const { user } = useAuth()
  const { showToast } = useNotifications()
  const daeId = user?.isAuthenticated ? user.principalId : undefined

  const [modalOpen, setModalOpen] = useState(false)
  const [pollingReportId, setPollingReportId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const { data: reportsData, isLoading } = useAuditReportsList()
  const allReports = useMemo(() => reportsData?.reports ?? [], [reportsData])

  // Client-side filtering by status or format
  const filteredReports = useMemo(() => {
    if (!searchTerm) return allReports
    const term = searchTerm.toLowerCase()
    return allReports.filter((r) => {
      const filterSummary = formatFilters(r.filters).toLowerCase()
      return (
        r.status.toLowerCase().includes(term) ||
        r.format.toLowerCase().includes(term) ||
        filterSummary.includes(term)
      )
    })
  }, [allReports, searchTerm])

  // Pagination
  const pageCount = Math.max(1, Math.ceil(filteredReports.length / ITEMS_PER_PAGE))
  const page = Math.min(currentPage, pageCount)
  const paginatedReports = filteredReports.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  // Poll a newly-created report until it completes
  const { data: pollingReport } = useAuditReport(pollingReportId)

  useEffect(() => {
    if (pollingReport && pollingReport.status !== "IN_PROGRESS") {
      setPollingReportId(null)
      if (pollingReport.status === "COMPLETE") {
        showToast({
          subject: "Report ready",
          message: "Your audit report is ready for download",
          variant: "success",
        })
      } else {
        showToast({
          subject: "Report failed",
          message: "Audit report generation failed",
          variant: "error",
        })
      }
    }
  }, [pollingReport, showToast])

  // Load studies for the modal
  const { data: studiesData } = useStudiesList(daeId)
  const studies = useMemo(
    () =>
      (studiesData?.pages.flatMap((p) => p.studies) ?? []).map((s) => ({
        studyId: s.study_id,
        name: s.study_name,
      })),
    [studiesData],
  )

  const handleReportCreated = (reportId: string) => {
    setPollingReportId(reportId)
    showToast({
      subject: "Report started",
      message: "Generation in progress — check back shortly",
      variant: "default",
    })
  }

  const handleDownload = (reportId: string, format: string) => {
    void auditApiService
      .getReport(reportId)
      .then((job) => {
        if (!job.downloadUrl) {
          throw new Error("Report is not ready for download")
        }
        return downloadUrlAsFile(job.downloadUrl, `audit-report-${reportId}.${format}`)
      })
      .catch(() => {
        showToast({
          subject: "Download failed",
          message: "Could not download the audit report. Please try again.",
          variant: "error",
        })
      })
  }

  return (
    <>
      <Breadcrumb
        items={[{ label: "Dashboard", path: "/dae/dashboard" }, { label: "Audit Reports" }]}
      />
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Generate and download compliance audit reports"
            counter={`(${filteredReports.length})`}
            actions={
              <Button variant="primary" iconName="add-plus" onClick={() => setModalOpen(true)}>
                Generate Report
              </Button>
            }
          >
            Audit Reports
          </Header>
        }
      >
        <Container>
          {isLoading ? (
            <Box textAlign="center" padding="xxl">
              <SpaceBetween size="s" alignItems="center">
                <Spinner size="large" />
                <span>Loading reports...</span>
              </SpaceBetween>
            </Box>
          ) : allReports.length === 0 ? (
            <Box textAlign="center" padding="xxl" color="text-status-inactive">
              <SpaceBetween size="xs">
                <Box variant="strong" color="inherit">
                  No reports generated yet
                </Box>
                <span>Click "Generate Report" to create your first compliance report.</span>
              </SpaceBetween>
            </Box>
          ) : (
            <Table<AuditReportListItem>
              variant="embedded"
              items={paginatedReports}
              trackBy="reportId"
              ariaLabels={{ tableLabel: "Audit reports" }}
              filter={
                <TextFilter
                  filteringText={searchTerm}
                  filteringPlaceholder="Search by status, format, or filters..."
                  filteringAriaLabel="Search reports"
                  onChange={({ detail }) => {
                    setSearchTerm(detail.filteringText)
                    setCurrentPage(1)
                  }}
                />
              }
              pagination={
                filteredReports.length > ITEMS_PER_PAGE ? (
                  <Pagination
                    currentPage={page}
                    pageCount={pageCount}
                    onPageChange={setCurrentPage}
                    onNext={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
                    onPrevious={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  />
                ) : undefined
              }
              empty={
                searchTerm ? (
                  <Box textAlign="center" padding="l">
                    No reports match your search.
                  </Box>
                ) : undefined
              }
              columnDefinitions={[
                {
                  id: "status",
                  header: "Status",
                  width: 140,
                  cell: (item) => (
                    <Badge color={statusBadgeColor(item.status)}>{item.status}</Badge>
                  ),
                },
                {
                  id: "created",
                  header: "Created",
                  width: 200,
                  cell: (item) => formatDate(item.createdAt),
                },
                {
                  id: "filters",
                  header: "Filters",
                  cell: (item) => (
                    <Box color="text-body-secondary" fontSize="body-s">
                      {formatFilters(item.filters)}
                    </Box>
                  ),
                },
                {
                  id: "format",
                  header: "Format",
                  width: 80,
                  cell: (item) => item.format.toUpperCase(),
                },
                {
                  id: "actions",
                  header: "Actions",
                  width: 120,
                  cell: (item) =>
                    item.status === "COMPLETE" ? (
                      <Button
                        variant="inline-link"
                        iconName="download"
                        onClick={() => handleDownload(item.reportId, item.format)}
                      >
                        Download
                      </Button>
                    ) : item.status === "IN_PROGRESS" ? (
                      <Box color="text-status-info" fontSize="body-s">
                        <Spinner size="normal" /> Generating...
                      </Box>
                    ) : (
                      <Box color="text-status-error" fontSize="body-s">
                        Failed
                      </Box>
                    ),
                },
              ]}
            />
          )}
        </Container>
      </ContentLayout>

      <AuditReportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        studies={studies}
        onSuccess={handleReportCreated}
      />
    </>
  )
}
